import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ContentSyncCheckbox,
  ContentSyncState,
  ContentSyncToolbar,
  useContentSync
} from "../components/admin/ContentSyncControls";

function AdminSolutionGraphs({ apiBase, token }) {
  const navigate = useNavigate();
  const [graphs, setGraphs] = useState([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [frameFilter, setFrameFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState({ type: "", text: "" });
  const [deleting, setDeleting] = useState(new Set());
  const [archiving, setArchiving] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const ROWS_PER_PAGE = 25;

  useEffect(() => {
    let mounted = true;

    async function loadGraphs() {
      setLoading(true);
      setNotice({ type: "", text: "" });
      try {
        const response = await fetch(`${apiBase}/api/admin/solution-graphs`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.error || "Failed to load solution graphs");
        }
        if (mounted) {
          setGraphs(Array.isArray(payload.graphs) ? payload.graphs : []);
        }
      } catch (err) {
        if (mounted) {
          setNotice({ type: "error", text: err.message || "Failed to load solution graphs" });
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    if (token) {
      loadGraphs();
    }

    return () => {
      mounted = false;
    };
  }, [apiBase, token]);

  useEffect(() => {
    setCurrentPage(1);
  }, [graphs, filter, statusFilter, frameFilter, typeFilter]);

  async function handleArchive(event, graphId) {
    event.stopPropagation();
    if (!window.confirm("Archive this graph? It will no longer be used for grading. You can delete it afterwards.")) return;
    setArchiving((prev) => new Set([...prev, graphId]));
    setNotice({ type: "", text: "" });
    try {
      const res = await fetch(`${apiBase}/api/admin/solution-graphs/${graphId}/archive`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.error || "Archive failed");
      setGraphs((prev) => prev.map((g) => (g._id === graphId ? { ...g, status: "archived" } : g)));
      setNotice({ type: "success", text: "Graph archived. You can now delete it if needed." });
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Archive failed" });
    } finally {
      setArchiving((prev) => {
        const next = new Set(prev);
        next.delete(graphId);
        return next;
      });
    }
  }

  async function handleDelete(event, graphId) {
    event.stopPropagation();
    if (!window.confirm("Delete this archived version? This cannot be undone.")) return;
    setDeleting((prev) => new Set([...prev, graphId]));
    setNotice({ type: "", text: "" });
    try {
      const res = await fetch(`${apiBase}/api/admin/solution-graphs/${graphId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.error || "Delete failed");
      setGraphs((prev) => prev.filter((g) => g._id !== graphId));
      setNotice({ type: "success", text: "Archived version deleted." });
    } catch (err) {
      setNotice({ type: "error", text: err.message || "Delete failed" });
    } finally {
      setDeleting((prev) => {
        const next = new Set(prev);
        next.delete(graphId);
        return next;
      });
    }
  }

  const frameOptions = useMemo(() => {
    const seen = new Map();
    graphs.forEach((graph) => {
      if (graph.isLibraryGraph) return;
      const id = String(graph.frameId?._id || "");
      if (!id || seen.has(id)) return;
      seen.set(id, graph.frameId?.title || "Frame");
    });
    return Array.from(seen.entries());
  }, [graphs]);

  const filteredGraphs = useMemo(() => {
    const query = filter.trim().toLowerCase();
    return graphs.filter((graph) => {
      const matchesText =
        !query ||
        String(graph.title || "").toLowerCase().includes(query) ||
        String(graph.description || "").toLowerCase().includes(query) ||
        String(graph.frameId?.title || "").toLowerCase().includes(query) ||
        String(graph.chapterId || "").toLowerCase().includes(query);
      const matchesStatus = statusFilter === "all" || String(graph.status || "") === statusFilter;
      const matchesFrame = frameFilter === "all" || String(graph.frameId?._id || "") === frameFilter;
      const matchesType =
        typeFilter === "all" ||
        (typeFilter === "library" && graph.isLibraryGraph) ||
        (typeFilter === "frame" && !graph.isLibraryGraph);
      return matchesText && matchesStatus && matchesFrame && matchesType;
    });
  }, [graphs, filter, statusFilter, frameFilter, typeFilter]);

  const syncItems = useMemo(
    () => graphs.map((graph) => ({ contentType: "graph", contentId: String(graph._id) })),
    [graphs]
  );
  const contentSync = useContentSync({ apiBase, token, items: syncItems });
  const visibleSyncItems = useMemo(
    () => filteredGraphs.map((graph) => ({ contentType: "graph", contentId: String(graph._id) })),
    [filteredGraphs]
  );

  const totalPages = Math.ceil(filteredGraphs.length / ROWS_PER_PAGE);
  const paginatedGraphs = useMemo(() => {
    const start = (currentPage - 1) * ROWS_PER_PAGE;
    const end = start + ROWS_PER_PAGE;
    return filteredGraphs.slice(start, end);
  }, [filteredGraphs, currentPage, ROWS_PER_PAGE]);

  return (
    <div className="admin-panel">
      <div className="admin-toolbar">
        <input
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Filter graphs"
        />
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value="all">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
        <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
          <option value="all">All Types</option>
          <option value="frame">Frame Graphs</option>
          <option value="library">Library Graphs</option>
        </select>
        <select value={frameFilter} onChange={(event) => setFrameFilter(event.target.value)}>
          <option value="all">All Frames</option>
          {frameOptions.map(([id, title]) => (
            <option key={id} value={id}>
              {title}
            </option>
          ))}
        </select>
        {contentSync.canAuthorContent && (
          <>
            <button
              className="secondary-button"
              type="button"
              onClick={() => navigate("/admin/solution-graphs/new", { state: { isLibraryGraph: true } })}
            >
              New Library Graph
            </button>
            <button className="primary-button" type="button" onClick={() => navigate("/admin/solution-graphs/new")}>
              Create Graph
            </button>
          </>
        )}
      </div>

      <ContentSyncToolbar sync={contentSync} visibleItems={visibleSyncItems} />

      {notice.text && (
        <div className={`admin-notice ${notice.type === "error" ? "error" : "success"}`}>{notice.text}</div>
      )}

      {filteredGraphs.length > 0 && (
        <div className="pagination-controls">
          <button className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ← Prev
          </button>

          <span className="pagination-info">
            Page {currentPage} of {totalPages}
            &nbsp;·&nbsp;
            {filteredGraphs.length} rows total
          </span>

          <button className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              {contentSync.showSync && <th aria-label="Select for production sync" />}
              <th>S.No</th>
              <th>Frame / Type</th>
              <th>Graph</th>
              <th>Version</th>
              <th>Status</th>
              <th>Nodes</th>
              <th>Paths</th>
              <th>Publish Ready</th>
              <th>Updated</th>
              {contentSync.showSync && <th>Production</th>}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedGraphs.map((graph, index) => {
              const syncItem = { contentType: "graph", contentId: String(graph._id) };
              return (
                <tr
                  key={graph._id}
                  className={contentSync.isSelected(syncItem) ? "selected" : ""}
                  onClick={() =>
                    navigate(`/admin/solution-graphs/${graph._id}`, {
                      state: { graph }
                    })
                  }
                >
                  {contentSync.showSync && (
                    <td onClick={(event) => event.stopPropagation()}>
                      <ContentSyncCheckbox
                        sync={contentSync}
                        item={syncItem}
                        label={`Select graph ${graph.title} for production sync`}
                      />
                    </td>
                  )}
                  <td>{(currentPage - 1) * ROWS_PER_PAGE + index + 1}</td>
                <td>
                  {graph.isLibraryGraph ? (
                    <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      <span className="solution-graph-library-badge">Library</span>
                      {graph.frameId?.title && (
                        <span style={{ fontSize: 11, color: "#6b7280" }}>{graph.frameId.title}</span>
                      )}
                    </span>
                  ) : (
                    graph.frameId?.title || "—"
                  )}
                </td>
                <td>{graph.title || "Untitled Graph"}</td>
                <td>{graph.version || 1}</td>
                <td>{graph.status || "draft"}</td>
                <td>{graph.graphStats?.nodeCount || 0}</td>
                <td>{graph.graphStats?.pathCount || 0}</td>
                <td>{graph.graphStats?.publishReady ? "Yes" : "No"}</td>
                <td>{graph.updatedAt ? new Date(graph.updatedAt).toLocaleDateString() : "-"}</td>
                {contentSync.showSync && (
                  <td><ContentSyncState sync={contentSync} item={syncItem} /></td>
                )}
                <td onClick={(e) => e.stopPropagation()}>
                  {contentSync.canAuthorContent && graph.status !== "archived" && (
                    <button
                      type="button"
                      className="secondary-button"
                      disabled={archiving.has(graph._id)}
                      onClick={(e) => handleArchive(e, graph._id)}
                    >
                      {archiving.has(graph._id) ? "Archiving..." : "Archive"}
                    </button>
                  )}
                  {contentSync.canAuthorContent && graph.status === "archived" && (
                    <button
                      type="button"
                      className="danger-button"
                      disabled={deleting.has(graph._id)}
                      onClick={(e) => handleDelete(e, graph._id)}
                    >
                      {deleting.has(graph._id) ? "Deleting..." : "Delete"}
                    </button>
                  )}
                </td>
                </tr>
              );
            })}
            {loading && (
              <tr>
                <td colSpan={contentSync.showSync ? 12 : 10}>Loading solution graphs...</td>
              </tr>
            )}
            {!filteredGraphs.length && !loading && (
              <tr>
                <td colSpan={contentSync.showSync ? 12 : 10}>No solution graphs found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminSolutionGraphs;
